import {
    Edit3, Trash2, Search, MoreVertical, MessageSquarePlus,
} from "lucide-react";
import { useEffect, useState } from "react";

export default function ChatHistoryPanel({
    chatList, search, setSearch, setActiveChat,
    menuOpen, setMenuOpen, handleRenameChat, handleDeleteChat,
    handleCreateNewChat, activeChat, navigate,
}) {
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState("");
    const prettyModel = (raw) => {
        const value = String(raw || "").toLowerCase();
        if (!value) return "AI Assistant";
        if (value.includes("openai/") || value.includes("gpt")) return "OpenAI";
        if (value.includes("claude") || value.includes("anthropic/")) return "Claude";
        if (value.includes("perplexity")) return "Perplexity";
        if (value.includes("llama") || value.includes("ollama") || value.includes("oss-")) return "vLLM";
        return raw;
    };

    // ปิดเมนูเมื่อคลิกนอก
    useEffect(() => {
        const onDocClick = () => setMenuOpen(null);
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, [setMenuOpen]);

    const startEditing = (chat) => {
        setEditingId(chat.cid ?? chat.id);
        setEditingTitle(chat.title);
        setMenuOpen(null);
    };

    const stopEditing = () => {
        setEditingId(null);
        setEditingTitle("");
    };

    const saveEdit = (cid) => {
        if (!editingTitle.trim()) return stopEditing();
        handleRenameChat(cid, editingTitle.trim());
        stopEditing();
    };

    return (
        <div className="space-y-3">

            {/* ปุ่มเริ่มแชทใหม่ */}
            <button
                onClick={handleCreateNewChat}
                className="w-full flex items-center justify-center gap-2.5
          bg-gradient-to-r from-blue-600 via-violet-600 to-cyan-500 text-white hover:shadow-lg hover:shadow-blue-500/25
          text-[13px] font-semibold px-4 py-2.5 rounded-xl transition-all duration-300 group hover:-translate-y-0.5"
            >
                <MessageSquarePlus size={16} className="group-hover:scale-110 transition-transform duration-300" />
                <span>เริ่มแชทใหม่</span>
            </button>

            {/* ค้นหา */}
            <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-500 transition-colors" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ค้นหา..."
                    className="w-full text-[13px] pl-9 pr-3 py-2 rounded-xl border border-slate-200/80
            bg-white text-slate-700 placeholder:text-slate-400 shadow-sm
            focus:outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-500/10
            transition-all duration-300"
                />
            </div>

            {/* หัวข้อ */}
            <div className="flex items-center justify-between px-1 pt-2">
                <h2 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Recent Chats
                </h2>
                <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-md font-medium">
                    {chatList.length}
                </span>
            </div>

            {/* รายการแชท */}
            <div className="space-y-1.5 pb-4">
                {chatList
                    .filter(chat => (chat.title || "").toLowerCase().includes(search.toLowerCase()))
                    .map((chat) => {
                        const rowKey = chat.cid ?? chat.id;
                        const isActive = (activeChat?.cid ?? activeChat?.id) === rowKey;
                        const isOpen = menuOpen === rowKey;
                        const isEditing = editingId === rowKey;

                        return (
                            <div
                                key={rowKey}
                                onClick={() => !isEditing && navigate(`/c/${rowKey}`)}
                                className={`group relative flex items-center justify-between rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-300 border border-transparent
                  ${isActive
                                        ? "bg-white shadow-sm border-slate-200/80 ring-1 ring-black/5"
                                        : "hover:bg-white hover:shadow-sm hover:border-slate-200/60"
                                    }`}
                            >
                                {isActive && (
                                    <div className="absolute inset-y-2 left-0 w-1 bg-gradient-to-b from-blue-500 to-violet-500 rounded-r-full shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
                                )}
                                <div className="flex flex-col pr-8 w-full min-w-0">
                                    {isEditing ? (
                                        <input
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            onBlur={() => saveEdit(rowKey)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") saveEdit(rowKey);
                                                if (e.key === "Escape") stopEditing();
                                            }}
                                            autoFocus
                                            className="text-[13px] font-medium text-slate-700 border border-violet-300 rounded-md px-2 py-1 w-full focus:ring-2 focus:ring-violet-100 outline-none bg-white"
                                        />
                                    ) : (
                                        <div className={`text-[13px] font-semibold truncate transition-colors ${isActive ? "text-violet-900" : "text-slate-700 group-hover:text-slate-900"}`}>
                                            {chat.title_short || chat.title}
                                        </div>
                                    )}
                                    <div className="mt-1">
                                        <span className={`inline-flex items-center rounded-full px-1.5 py-[2px] text-[9px] font-medium border
                                            ${isActive
                                                ? "bg-white/60 border-violet-200/60 text-violet-700"
                                                : "bg-white border-slate-200/60 text-slate-400 group-hover:text-slate-500"
                                            }`}>
                                            {prettyModel(chat.model)}
                                        </span>
                                    </div>
                                </div>

                                {/* ปุ่มเมนู */}
                                {!isEditing && (
                                    <button
                                        className={`absolute right-2 p-1.5 rounded-lg transition-all duration-300
                                            ${isActive ? "opacity-100 text-violet-400 hover:bg-violet-100 hover:text-violet-600" : "opacity-0 group-hover:opacity-100 text-slate-400 hover:bg-slate-100 hover:text-slate-600"}
                                            md:opacity-0 md:group-hover:opacity-100`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMenuOpen(isOpen ? null : rowKey);
                                        }}
                                    >
                                        <MoreVertical size={16} />
                                    </button>
                                )}

                                {/* เมนูดรอปดาวน์ */}
                                {isOpen && (
                                    <div
                                        className="absolute right-2 top-9 w-36 bg-white/95 backdrop-blur-sm
                      border border-slate-200 rounded-lg shadow-lg z-40"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm hover:bg-[#f0f4ff]"
                                            onClick={() => startEditing(chat)}
                                        >
                                            <Edit3 size={16} className="text-gray-600" />
                                            <span>เปลี่ยนชื่อ</span>
                                        </button>

                                        <button
                                            className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                            onClick={() => { setMenuOpen(null); handleDeleteChat(rowKey); }}
                                        >
                                            <Trash2 size={16} className="text-red-600" />
                                            <span>ลบ</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}
